import React from 'react';

import _ from 'lodash';
import moment from 'moment';

import { Row } from 'grab-ui';
import { Accordion, Dropdown, Icon, Popup } from 'semantic-grab-ui';

import { getActionsList } from 'omega-rtc/utils/new-trident-utils';
import SubActionMetaForm from 'omega-rtc/components/form/trident-campaigns/SubActionMetaForm';
import { TridentCampaignFormData } from 'omega-rtc/types/ucd/campaign';
import { getActionErrorDisplay } from 'omega-rtc/utils/ucd-utils';
import deleteIcon from 'img/delete_icon.svg';

import './ScenarioPage.scss';

type Props = {
  targetUserType: string,
  campaign: any,
  formData: TridentCampaignFormData,
  handleFormDataChange: Function,
  listRuleTypes: any,
  errors: any, // Map of Error
  validateFormRef: Function
};

const DEFAULT_SCENARIO_NODE_ID = 1;
const DEFAULT_ACTION_NODE_ID = 3;

export class ActionSection extends React.Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.props.validateFormRef(this.validateForm);

    this.state = {
      actionActiveIndex: 0,
    };
    this.actionForm = {};
  }

  handleActionTitleClick = (e: any, titleProps: any) => {
    const { index } = titleProps;
    const { actionActiveIndex } = this.state;
    const newIndex = actionActiveIndex === index ? actionActiveIndex : index;
    this.setState({ actionActiveIndex: newIndex });
  }

  handleDeleteSubAction = (index: number) => {
    const actions = _.get(this.props.formData, 'actions', []);
    actions.splice(index, 1);
    this.props.handleFormDataChange({ actions });
  };

  handleAddSubAction = (event: any, data: any) => {
    const actionType = data.value;

    let newAction = {
      type: actionType,
      meta: {},
    };
    if (actionType === 'awardReward' || actionType === 'bulkAwardReward') {
      newAction = { ...newAction, isValid: false };
    }
    if (actionType === 'sendEmail' || actionType === 'bulkSendEmail') {
      newAction = { ...newAction, replyToEmail: true };
    }
    const actions = _.get(this.props.formData, 'actions', []);
    actions.push(newAction);
    this.props.handleFormDataChange({ actions });
  };

  // return format:  {nodes: 3: { type: actions, data: { type: awardReward, meta : {....} } } }
  constructTridentNode = (actions: Array<any>, campaignStartTime: string, campaignEndTime: string, targetUserType: string) => {
    const momentFields = [
      'meta.updateSegMembershipPayload.segmentExpiryDate',
    ]; // List of fields on the action node to be converted to moment data type

    const actionNodeData = _.map(actions, (actionData, key) => {
      _.forEach(momentFields, field => {
        const fieldValue = _.get(actionData, field);
        if (!_.isEmpty(fieldValue)) {
          _.set(actionData, field, moment(fieldValue));
        }
      });
      return { ...actionData, id: key };
    });
    return {
      targetUserType,
      nodes: {
        1: {
          type: 'scenarios',
          data: {
            meta: {
              startTime: moment(campaignStartTime),
              endTime: moment(campaignEndTime),
            },
          },
        },
        [DEFAULT_ACTION_NODE_ID]: {
          type: 'actions',
          data: actionNodeData,
        },
      },
    };
  };

  onActionFormChange = (entity: Object) => {
    const tridentActionEntity = _.get(entity, `nodes.${DEFAULT_ACTION_NODE_ID}.data`);
    const actionData = {
      actions: tridentActionEntity,
    };
    this.props.handleFormDataChange({ ...actionData });
  };

  validateForm = () => {
    const errorSubActionMap = {};
    const keys = _.keysIn(this.actionForm);
    keys.forEach((key) => {
      let form = this.actionForm[key];
      if (form !== null && _.isFunction(form.getWrappedInstance)) {
        form = form.getWrappedInstance();
      }
      if (form !== null && _.isFunction(form.getFormErrors)) {
        const errors = form.getFormErrors();
        if (errors.length > 0) {
          errorSubActionMap[key] = errors;
        }
      }
    });
    return errorSubActionMap;
  };

  render() {
    const { formData, listRuleTypes, targetUserType, selectedEventType, campaign } = this.props;

    // Actions
    const actionTypeOptions = getActionsList(listRuleTypes, targetUserType, selectedEventType);
    const rootActionOptions = actionTypeOptions.filter(a => a.group === '');
    const actionGroups = _.groupBy(actionTypeOptions.filter(a => a.group !== ''), 'group');
    const { actionActiveIndex } = this.state;
    const actions = _.get(formData, 'actions', []); // [ {type:onRideComplete, meta:{} ]

    // As we're using trident common component, we need to convert to trident model payload.
    const tridentNode = this.constructTridentNode(
      actions, _.get(campaign, 'start', ''), _.get(campaign, 'end', ''), targetUserType
    );
    const parentKeyList = [DEFAULT_SCENARIO_NODE_ID, DEFAULT_ACTION_NODE_ID]; // To be passed to trident common component

    const errors = _.get(this.props, 'errors', {});
    const buttonText = _.isEmpty(actions) ? 'Add Action' : 'Add More Action';

    const renderActionItem = actionTypeData => (
      <Dropdown.Item
        key={actionTypeData.type}
        value={actionTypeData.type}
        onClick={this.handleAddSubAction}
      >
        {`${actionTypeData.noun} `}
        {actionTypeData.description && <Popup
          trigger={<Icon name="info circle" />}
          content={actionTypeData.description}
          size="mini"
        />}
      </Dropdown.Item>
    );

    return (
      <Row className={'sub-section-row'}>
        <h3>Trigger</h3>
        <h4>Action to Trigger <span className={'red-asterisk'}>*</span></h4>


        {/* ------ Action List -----*/}
        {!_.isEmpty(actions) &&
          <Accordion fluid styled>
            {actions.map((subAction, index) => {
              const actionName = _.get(_.find(actionTypeOptions, { type: subAction.type }, {}), 'noun', subAction.type);

              return (
                <div key={index}>
                  <Accordion.Title
                    active={actionActiveIndex === index} index={index}
                    onClick={this.handleActionTitleClick} key={`${index}${'title-actionData'}`}
                  >
                    <Icon name="dropdown" />
                    <button className={'delete-button'} onClick={() => this.handleDeleteSubAction(index)}>
                      <img src={deleteIcon} height={'17px'} width={'15px'} alt={''} />
                    </button>
                    {actionName}

                    {!_.isEmpty(errors[index]) &&
                      getActionErrorDisplay(errors[index])
                    }
                  </Accordion.Title>
                  <Accordion.Content active={actionActiveIndex === index} key={`${index}content-actionData`}>
                    <SubActionMetaForm
                      key={`${index}-actionForm`}
                      onModelChange={this.onActionFormChange}
                      model={tridentNode}
                      currentNode={DEFAULT_ACTION_NODE_ID}
                      parentKeyList={parentKeyList}
                      subActionIndex={index}
                      actionType={subAction.type}
                      eventType={selectedEventType}
                      targetUserType={targetUserType}
                      formRef={form => (this.actionForm[index] = form)}
                      // enableDynamicPoint={this.props.enableDynamicPoint} TODO: Find out when testing
                    />
                  </Accordion.Content>
                </div>
              );
            })}
          </Accordion>
        }

        {/* ------ Add Action Button -----*/}
        <Dropdown className="rule-menu" text={buttonText} simple>
          <Dropdown.Menu className="right">
            {_.map(actionGroups, (val, key) => (
              <Dropdown.Item key={key}>
                <Icon name="triangle right" />
                <span className="text">{key}</span>
                <Dropdown.Menu className="right">
                  {_.map(val, renderActionItem)}
                </Dropdown.Menu>
              </Dropdown.Item>
            ))}
            {_.map(rootActionOptions, renderActionItem)}
          </Dropdown.Menu>
        </Dropdown>
      </Row>
    );
  }
}


export default ActionSection;
